import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { motion, useReducedMotion } from "motion/react";
import Link from "next/link";
import { App, ProviderType, ServiceType, User } from "@/types";
import { Template } from "@/types/template";
import useSWR from "swr";
import { formatMarkdown } from "@/components/formatMarkdown";
import { getTagLabel, getTemplateSourceLabel } from "@/components/launch/ListTemplateCard";
import { MarketingLink } from "@/components/OptionalLink";
import { useMemo, type ComponentType, type ReactNode } from "react";
import { TenantPlan } from "@/lib/plan";
import { Icon } from "@/components/landing/Navbar";
import {
  ArrowLeft,
  ArrowUpRight,
  CalendarPlus,
  Clock,
  Cpu,
  GitFork,
  HardDrive,
  History,
  Loader2,
  MemoryStick,
  Microchip,
  Code2,
  MonitorPlay,
  Server
} from "lucide-react";
import { serviceIcons } from "@/components/launch/ServiceLogos";

/** Ease-out-quart — smooth deceleration for the page-load reveal, no bounce. */
const EASE_OUT = [0.22, 1, 0.36, 1] as const;

/** Staggers the page-load reveal of its direct children. */
const revealContainer = {
  hidden: {},
  show: { transition: { staggerChildren: 0.08, delayChildren: 0.04 } }
};

/** Formats an ISO template timestamp as a short, human readable date (e.g. "Dec 1, 2025"). */
const formatTemplateDate = (value?: string) => {
  if (!value) {
    return null;
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return null;
  }

  return date.toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric"
  });
};

const serviceLabels: Record<ServiceType, string> = {
  redis: "Redis",
  postgres: "PostgreSQL",
  docker: "Docker",
  ddagent: "DD Agent",
  mariadb: "MariaDB",
  mongodb: "MongoDB",
  firewall: "Firewall",
  luks: "Disk Encryption",
  custom: "Custom"
};

/** Builds the link to the template source, pointing at the sub path when present. */
const getSourceCodeUrl = (app: Template["app"]) => {
  if (!app.sourcePath) {
    return app.repositoryUrl;
  }

  const base = app.repositoryUrl.endsWith("/") ? app.repositoryUrl : app.repositoryUrl + "/";
  const path = app.sourcePath.startsWith("/") ? app.sourcePath.slice(1) : app.sourcePath;
  return `${base}tree/${app.sourceBranch ?? "main"}/${path}`;
};

/** Outline action that opens in a new tab, with a leading glyph and a trailing external hint. */
const ActionLink = ({
  href,
  icon: Icon,
  children
}: {
  href: string;
  icon: ComponentType<{ className?: string; strokeWidth?: number }>;
  children: ReactNode;
}) => (
  <Button variant="outline" asChild className="w-full justify-start">
    <a href={href} target="_blank" rel="noopener noreferrer">
      <Icon className="size-4" strokeWidth={1.5} />
      <span className="flex-1 text-left">{children}</span>
      <ArrowUpRight className="size-3.5 text-muted-foreground" strokeWidth={1.5} />
    </a>
  </Button>
);

/** "Fork this template" action pointing at GitHub's repository templating flow. */
const TemplateForkButton = ({ url: repositoryUrl }: { url: string }) => {
  const { repositoryOwner, repositoryName } = useMemo(() => {
    const url = new URL(repositoryUrl);
    if (url.hostname !== "github.com") {
      return {};
    }

    const path = url.pathname.split("/");
    return {
      repositoryOwner: path[1],
      repositoryName: path[2]
    };
  }, [repositoryUrl]);

  return (
    <ActionLink
      href={`https://github.com/new?template_name=${repositoryName}&template_owner=${repositoryOwner}`}
      icon={GitFork}
    >
      Fork this template
    </ActionLink>
  );
};

/** Plan aware deploy call to action: sign in, subscribe, deploy or upgrade. */
const DeployButton = ({
  template,
  user,
  tenant,
  apps,
  provider,
  loading
}: {
  template: Template;
  user?: Pick<User, "id"> | null;
  tenant?: TenantPlan;
  apps?: App[];
  provider?: ProviderType;
  loading?: boolean;
}) => {
  const subscribed = tenant && tenant.maxApps > 0;
  const providerQuery = provider ? `?provider=${provider}` : "";

  if (loading) {
    return (
      <Button size="lg" className="w-full" disabled>
        <Loader2 className="h-5 w-5 animate-spin" strokeWidth={1.5} />
        Loading…
      </Button>
    );
  }

  return (
    <Button asChild size="lg" className="w-full">
      <MarketingLink
        href={
          !user || !tenant || !apps
            ? `/auth/signIn?callbackUrl=/r/${template.id}${providerQuery}`
            : !subscribed
              ? "/pricing"
              : apps.length + 1 <= tenant.maxApps
                ? `/r/${template.id}/host${providerQuery}`
                : "/pricing"
        }
        className={`plausible-event-name=DeployTemplateNow plausible-event-template=${template.id}`}
      >
        <Icon className="h-5 w-5" light />
        {!user || !tenant || !apps
          ? "Sign In to Deploy"
          : !subscribed
            ? "Subscribe to Deploy"
            : apps.length + 1 <= tenant.maxApps
              ? "Deploy Now"
              : "Upgrade to Deploy"}
      </MarketingLink>
    </Button>
  );
};

/** Helper line under the deploy button, explaining the current plan-aware action. */
const deployHint = (
  user?: Pick<User, "id"> | null,
  tenant?: TenantPlan,
  apps?: App[],
  loading?: boolean
) => {
  if (loading) {
    return "Checking your plan…";
  }
  if (!user || !tenant || !apps) {
    return "Sign in to deploy to your own server.";
  }
  if (!tenant.maxApps) {
    return "Subscribe to start deploying apps.";
  }
  if (apps.length + 1 <= tenant.maxApps) {
    return "Runs on your own server. No SSH required.";
  }
  return `You've used all ${tenant.maxApps} apps on your plan — upgrade for more.`;
};

/**
 * One labelled spec line with a leading glyph. Numeric values (memory, CPU, storage)
 * use mono so digits align; descriptive values stay in the body face.
 */
const SpecRow = ({
  icon: Icon,
  label,
  mono = false,
  children
}: {
  icon: ComponentType<{ className?: string; strokeWidth?: number }>;
  label: string;
  mono?: boolean;
  children: ReactNode;
}) => (
  <div className="flex items-center justify-between gap-3 py-2.5 text-sm">
    <span className="flex items-center gap-2 text-muted-foreground">
      <Icon className="size-4 shrink-0" strokeWidth={1.5} />
      {label}
    </span>
    <span className={`text-right text-foreground ${mono ? "font-mono" : ""}`}>{children}</span>
  </div>
);

const SpecGroup = ({ title, children }: { title: string; children: ReactNode }) => (
  <div>
    <p className="mb-1.5 text-xs font-medium uppercase tracking-wider text-muted-foreground">
      {title}
    </p>
    {children}
  </div>
);

export const TemplateCard = ({
  template,
  user,
  provider
}: {
  template: Template;
  user?: Pick<User, "id"> | null;
  provider?: ProviderType;
}) => {
  const { data: tenant, isLoading: tenantLoading } = useSWR<TenantPlan>(
    user?.id ? "/api/tenant/plan" : null
  );
  const { data: apps, isLoading: appsLoading } = useSWR<App[]>(user?.id ? "/api/app" : null);
  // Logged in but plan/apps still resolving — distinct from genuinely signed out, so the
  // CTA doesn't flash "Sign In to Deploy" at a returning subscriber.
  const accountLoading = Boolean(user?.id) && (tenantLoading || appsLoading);

  const reduceMotion = useReducedMotion();
  const reveal = reduceMotion
    ? { hidden: { opacity: 1 }, show: { opacity: 1 } }
    : {
        hidden: { opacity: 0, y: 14 },
        show: { opacity: 1, y: 0, transition: { duration: 0.5, ease: EASE_OUT } }
      };

  const requirements = template.requirements;
  const hasRequirements =
    requirements &&
    (requirements.memory != null ||
      requirements.cpu != null ||
      requirements.storage != null ||
      requirements.gpu);

  const sourceLabel = template.source
    ? getTemplateSourceLabel(template.source) || "Test"
    : null;

  const createdAt = formatTemplateDate(template.createdAt);
  const updatedAt = formatTemplateDate(template.updatedAt);

  return (
    <motion.div
      className="container mx-auto max-w-6xl px-4 py-8 sm:py-10"
      variants={revealContainer}
      initial="hidden"
      animate="show"
    >
      <motion.div variants={reveal}>
        <Link
          href="/r"
          className="link mb-8 inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="size-4" strokeWidth={1.5} />
          All templates
        </Link>
      </motion.div>

      <motion.div
        variants={reveal}
        className="mt-8 grid grid-cols-1 gap-10 lg:grid-cols-3 lg:items-start"
      >
        {/* Hero: name, intro, icon and tags. Always first so it leads on mobile. */}
        <header className="flex flex-col items-center gap-5 text-center sm:flex-row sm:items-center sm:text-left lg:col-span-2 lg:col-start-1">
          {template.logo && (
            <div className="flex size-20 shrink-0 items-center justify-center rounded-2xl border bg-card p-2 shadow-sm">
              <img
                src={template.logo}
                alt={`${template.name} logo`}
                loading="lazy"
                className="size-full object-contain"
              />
            </div>
          )}
          <div className="space-y-3">
            <h1 className="font-serif text-4xl font-normal tracking-tight text-foreground sm:text-5xl">
              {template.name}
            </h1>
            <div className="max-w-2xl text-lg leading-relaxed text-secondary-foreground">
              {formatMarkdown(template.intro)}
            </div>
            <div className="flex flex-wrap justify-center gap-2 pt-1 sm:justify-start">
              {template.experimental && <Badge variant="destructive">Experimental</Badge>}
              {sourceLabel && <Badge variant="secondary">{sourceLabel}</Badge>}
              {template.tags?.toSorted().map(tag => (
                <Badge
                  key={tag}
                  variant="outline"
                  className="cursor-pointer hover:bg-primary hover:text-primary-foreground"
                  asChild
                >
                  <Link href={`/r?tag=${tag}`}>{getTagLabel(tag)}</Link>
                </Badge>
              ))}
            </div>
          </div>
        </header>

        {/* Action rail: deploy CTA and specs. After the hero in the DOM so it falls
            below the name/intro on mobile; pinned top-right on desktop via explicit grid
            placement (row 1, column 3). */}
        <aside className="space-y-8 lg:col-start-3 lg:row-span-2 lg:row-start-1 lg:sticky lg:top-24 lg:self-start">
          {/* Deploy card — the one anchored surface on the page; a brand-tinted
              border earns emphasis without a glow. */}
          <div className="space-y-4 rounded-xl border border-primary/25 bg-card p-5 shadow-sm">
            <div className="space-y-1.5">
              <DeployButton
                template={template}
                user={user}
                tenant={tenant}
                apps={apps}
                provider={provider}
                loading={accountLoading}
              />
              <p className="text-center text-xs text-muted-foreground">
                {deployHint(user, tenant, apps, accountLoading)}
              </p>
            </div>

            {(template.demoUrl ||
              template.app.repositoryUrl ||
              template.tags?.includes("template")) && (
              <div className="space-y-2 border-t pt-4">
                {template.demoUrl && (
                  <ActionLink href={template.demoUrl} icon={MonitorPlay}>
                    Live demo
                  </ActionLink>
                )}
                {template.app.repositoryUrl && (
                  <ActionLink href={getSourceCodeUrl(template.app)} icon={Code2}>
                    Source code
                  </ActionLink>
                )}
                {template.tags?.includes("template") && (
                  <TemplateForkButton url={template.app.repositoryUrl} />
                )}
              </div>
            )}
          </div>

          {/* Specs */}
          {(hasRequirements ||
            (template.services && template.services.length > 0) ||
            template.deployTime ||
            template.provider ||
            createdAt ||
            updatedAt) && (
            <div className="space-y-6 px-1">
              {hasRequirements && (
                <SpecGroup title="Requirements">
                  <div className="divide-y divide-border">
                    {requirements?.memory != null && (
                      <SpecRow icon={MemoryStick} label="Memory" mono>
                        {requirements.memory} MB
                      </SpecRow>
                    )}
                    {requirements?.cpu != null && (
                      <SpecRow icon={Cpu} label="CPU" mono>
                        {requirements.cpu} vCPU
                      </SpecRow>
                    )}
                    {requirements?.storage != null && (
                      <SpecRow icon={HardDrive} label="Storage" mono>
                        {requirements.storage} GB
                      </SpecRow>
                    )}
                    {requirements?.gpu && (
                      <SpecRow icon={Microchip} label="GPU" mono>
                        {requirements.gpu.count}× {requirements.gpu.model}
                      </SpecRow>
                    )}
                  </div>
                </SpecGroup>
              )}

              {template.services && template.services.length > 0 && (
                <SpecGroup title="What's included">
                  <div className="flex flex-wrap gap-2 pt-1">
                    {template.services.map(service => {
                      const ServiceIcon = serviceIcons[service];
                      return (
                        <Badge
                          key={service}
                          variant="secondary"
                          className="gap-1.5 py-1 [&>svg]:size-4 [&>svg]:text-muted-foreground"
                        >
                          {ServiceIcon && <ServiceIcon strokeWidth={1.5} />}
                          {serviceLabels[service] ?? service}
                        </Badge>
                      );
                    })}
                  </div>
                </SpecGroup>
              )}

              {(template.deployTime || template.provider || createdAt || updatedAt) && (
                <SpecGroup title="Details">
                  <div className="divide-y divide-border">
                    {template.deployTime && (
                      <SpecRow icon={Clock} label="Deploy time">
                        {template.deployTime}
                      </SpecRow>
                    )}
                    {template.provider && (
                      <SpecRow icon={Server} label="Provider">
                        {template.provider}
                      </SpecRow>
                    )}
                    {createdAt && (
                      <SpecRow icon={CalendarPlus} label="Added">
                        {createdAt}
                      </SpecRow>
                    )}
                    {updatedAt && (
                      <SpecRow icon={History} label="Updated">
                        {updatedAt}
                      </SpecRow>
                    )}
                  </div>
                </SpecGroup>
              )}
            </div>
          )}
        </aside>

        {/* Main column: description, video and about */}
        <div className="space-y-10 lg:col-span-2 lg:col-start-1">
          {template.description && (
            <section>
              <p className="mb-3 text-xl font-normal tracking-tight sm:text-2xl">
                About {template.name}
              </p>
              {formatMarkdown(
                template.description,
                undefined,
                undefined,
                true,
                "text-base leading-relaxed text-secondary-foreground"
              )}
            </section>
          )}

          {template.introVideoUrl && (
            <section>
              <h2 className="mb-3 text-xl font-normal tracking-tight sm:text-2xl">
                Introduction to {template.name}
              </h2>
              <iframe
                src={template.introVideoUrl}
                title={`Introduction to ${template.name}`}
                className="aspect-video w-full rounded-xl border shadow-sm"
                loading="lazy"
                referrerPolicy="strict-origin-when-cross-origin"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
              />
            </section>
          )}

          <section className="rounded-2xl bg-muted/50 dark:bg-muted/30 p-6">
            <div className="mb-2 flex items-center gap-2 text-muted-foreground">
              <Icon className="h-4 w-auto" />
              <h2 className="font-medium text-foreground">About DollarDeploy</h2>
            </div>
            <p className="leading-relaxed text-muted-foreground">
              DollarDeploy deploys and manages apps on your own VPS — no SSH, no YAML, no
              lock-in. Launch {template.name} in a few clicks, then get HTTPS, monitoring, logs
              and backups handled for you.
            </p>
          </section>
        </div>
      </motion.div>
    </motion.div>
  );
};
